import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Ticket } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { usePatientActivePunchCards } from '@/hooks/usePunchCardPayment';
import {
  StaffPaymentChoiceError,
  useStaffAppointmentPayment,
} from '@/hooks/useStaffAppointmentPayment';
import { pickPreferredPunchCard, selectApplicablePunchCards } from '@/lib/calendarPaymentChoice';

export function AppointmentChangePaymentPanel({
  clinicId,
  appointmentId,
  patientId,
  serviceId,
  currentLabel,
  onFinished,
}: {
  clinicId: string;
  appointmentId: string;
  patientId: string;
  serviceId: string | null;
  currentLabel: string;
  onFinished: () => void;
}) {
  const { t } = useTranslation();
  const payment = useStaffAppointmentPayment();
  const { data: activePunchCards, isFetched } = usePatientActivePunchCards(patientId, clinicId);
  const [selectedPunchCardId, setSelectedPunchCardId] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const applicableCards = useMemo(
    () => selectApplicablePunchCards(activePunchCards ?? [], serviceId),
    [activePunchCards, serviceId],
  );
  const preferredCard = pickPreferredPunchCard(applicableCards);
  const selectedCard =
    applicableCards.find((card) => card.id === selectedPunchCardId) ?? preferredCard;

  async function handleConfirm() {
    setErrorCode(null);
    const cardId = selectedCard?.id;
    if (!cardId) {
      setErrorCode('klip_failed');
      return;
    }
    try {
      await payment.replaceInvoiceWithKlip.mutateAsync({
        punchCardId: cardId,
        appointmentId,
        patientId,
        clinicId,
        clipsToUse: 1,
      });
      toast.success(t('appointments.payment_choice.klip_used', { count: 1 }));
      onFinished();
    } catch (error) {
      const code = error instanceof StaffPaymentChoiceError ? error.code : 'unknown';
      setErrorCode(code);
      if (code === 'invoice_paid_locked') {
        toast.error(t('appointments.details.change_payment_paid_locked'));
      } else if (code === 'already_settled' || code === 'already_paid') {
        toast.error(t('appointments.payment.already_paid'));
      } else {
        toast.error(t('appointments.payment.punch_card_payment_failed'));
      }
    }
  }

  return (
    <div className="space-y-3" data-appointment-change-payment-panel="">
      <h5 className={cn(TYPE.card, 'text-ds-text')}>
        {t('appointments.details.change_payment_method')}
      </h5>
      <p className={UI_MUTED}>
        <span className="font-medium">{t('appointments.details.change_payment_current')}:</span>{' '}
        {currentLabel}
      </p>

      {!isFetched ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-ds-muted" aria-hidden="true" />
        </div>
      ) : applicableCards.length === 0 ? (
        <p className={UI_MUTED}>{t('appointments.details.change_payment_no_clip')}</p>
      ) : (
        <div className="space-y-2">
          <p className={cn(TYPE.small, 'text-ds-muted')}>
            {t('appointments.details.change_payment_to')}
          </p>
          {applicableCards.length > 1 && (
            <label className="block">
              <span className={cn('mb-1 block', TYPE.small, 'text-ds-muted')}>
                {t('appointments.payment.select_punch_card')}
              </span>
              <select
                value={selectedCard?.id ?? ''}
                onChange={(e) => setSelectedPunchCardId(e.target.value)}
                className="ds-field"
              >
                {applicableCards.map((card) => (
                  <option key={card.id} value={card.id}>
                    {card.name} — {card.remaining_clips} {t('appointments.payment.clips_remaining')}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            disabled={payment.isPending || !selectedCard}
            onClick={() => void handleConfirm()}
            className="flex w-full items-center gap-3 rounded-lg border border-ds-line bg-ds-card px-3 py-2 text-left text-sm hover:bg-ds-row-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Ticket className="h-4 w-4 shrink-0 text-ds-teal" aria-hidden />
            <span>
              <span className="block font-medium text-ds-text">
                {t('appointments.payment.use_clip')}
              </span>
              <span className={cn('mt-0.5 block', UI_MUTED)}>
                {t('appointments.payment_choice.clips_remaining_count', {
                  count: selectedCard?.remaining_clips ?? 0,
                })}
              </span>
            </span>
          </button>
        </div>
      )}

      {errorCode === 'invoice_paid_locked' && (
        <p className="rounded-md bg-ds-amber-tint px-3 py-2 text-sm text-ds-amber-ink">
          {t('appointments.details.change_payment_paid_locked')}
        </p>
      )}

      <div className="flex justify-end border-t border-ds-line pt-3">
        <Button type="button" variant="secondary" onClick={onFinished} disabled={payment.isPending}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}
